import { useCallback, useEffect, useState } from 'react';

import i18next from 'i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import useCollections from '@/hooks/useCollections';
import useDatasources from '@/hooks/useDatasources';
import useDialogs from '@/hooks/useDialogs';
import { useBroadcasts } from '@/modules/broadcast/hooks/useBroadcasts';
import { useCampaigns } from '@/modules/campaigns/hooks/useCampaigns';
import { ResponseTypeFlags } from '@/modules/tryIt/hooks/useResponseTypeFlags';
import {
  selectIsSending,
  selectNodesStackByIndex,
  selectNodesStackParentNodeNameByIndex,
} from '@/modules/tryIt/redux/selectors';
import { popModal } from '@/redux/modals/actions';
import { requestNodeSelection } from '@/redux/nodes/actions';
import { selectAccountSlug } from '@/redux/session/selectors';
import { Dialog } from '@/shared/types/dialog';
import { DebugStepSubType, DebugStepType, NodeType, TryItCollection } from '@/shared/types/tryIt';

export const ResponseTypes = {
  BRAIN: 'brain',
  COLLECTION: 'collection',
  UNKNOWN: 'unknown',
  BROADCAST: 'broadcast',
  KNOWLEDGE: 'knowledge',
  CAMPAIGN: 'campaign',
  VOICEMAIL_DETECTED: 'voicemail_detected',
  INTERRUPTED: 'interrupted',
  AUTHENTICATION_CONTINUE: 'authentication_continue',
} as const;

const INITIAL_TITLE = { name: '', type: null };

// Response types
const brainResponse = (name: string) => ({ name, type: ResponseTypes.BRAIN });

const unknownResponse = (name: string = 'Unknown') => ({ name, type: ResponseTypes.UNKNOWN });

const knowledgeResponse = (name: string) => ({ name, type: ResponseTypes.KNOWLEDGE });

const collectionResponse = (collectionName: string) => ({
  name: collectionName,
  type: ResponseTypes.COLLECTION,
});

const broadcastResponse = (name: string) => ({ name: name, type: ResponseTypes.BROADCAST });

const campaignResponse = (name: string) => ({ name: name, type: ResponseTypes.CAMPAIGN });

const voicemailDetectedResponse = () => ({
  name: i18next.t('try_it.voicemail_detected_response'),
  type: ResponseTypes.VOICEMAIL_DETECTED,
});

const interruptedResponse = () => ({
  name: i18next.t('try_it.interrupted_response'),
  type: ResponseTypes.INTERRUPTED,
});

const authenticationContinueResponse = () => ({
  name: i18next.t('try_it.authentication_continue_response'),
  type: ResponseTypes.AUTHENTICATION_CONTINUE,
});

export const useMessageNode = (
  id: string,
  brainId: string,
  tryItCollection: TryItCollection,
  index: number,
  author_type: string,
  steps?: NodeType['steps'],
  entitiesMatched: boolean = false,
  responseTypeFlags?: ResponseTypeFlags,
  broadcastId?: string,
  campaignId?: string,
  nodeName?: string | null,
  nodeNodesStack?: NodeType['nodes_stack']
) => {
  const parentNodeNameFromSlice = useSelector(selectNodesStackParentNodeNameByIndex(index));
  const nodesStackFromSlice = useSelector(selectNodesStackByIndex(index));
  // Redesign turns live in state.tryIt (legacy selectors empty), so fall back to the node's props.
  const parentNodeName = parentNodeNameFromSlice || nodeName;
  const nodesStack = nodesStackFromSlice ?? nodeNodesStack;

  const isLoadingResponse = useSelector(selectIsSending);

  // Custom hooks
  const navigate = useNavigate();
  const slug = useSelector(selectAccountSlug);
  const dispatch = useDispatch();
  const { dialogs } = useDialogs(brainId);
  const { collection } = useCollections(tryItCollection?.collection_id);
  const { datasources } = useDatasources(tryItCollection?.collection_id);
  const { broadcast } = useBroadcasts({ broadcastId });
  const { campaign } = useCampaigns(campaignId);

  // Local state
  const [title, setTitle] = useState(INITIAL_TITLE);
  const [icons, setIcons] = useState([]);

  // Determine icons (only for collection responses currently)
  useEffect(() => {
    // Set icons for collection responses
    if (datasources) {
      const datasourceIds = tryItCollection?.fragments?.map((entry) => entry.datasource_id);
      const uniqueDatasourceIds = [...new Set(datasourceIds)];
      const iconTypes =
        uniqueDatasourceIds?.map((datasourceId) => {
          const foundDatasource = datasources?.find((d) => d.datasource_id === datasourceId);

          return foundDatasource?.type;
        }) ?? [];

      const uniqueIconTypes = [...new Set(iconTypes)];

      setIcons(uniqueIconTypes);
    }

    // Set icons for brain responses
    if (steps?.length > 0) {
      const filteredSteps = steps?.filter((step) => step?.action_type !== 'event');

      const finalIcons = filteredSteps?.map((step) => {
        let id = step?.action_type;

        // Cases where the action type is not used to determine the icon
        // WORKFLOW_TRIGGERED must be checked first to match BrainStep.tsx priority
        if (step?.sub_type === DebugStepSubType.WORKFLOW_TRIGGERED) {
          const triggeredNode = nodesStack?.find((n) => n.node_id === step?.node_id);
          id = triggeredNode?.type || 'event';
        } else if (step?.sub_type === DebugStepSubType.STARTING_NODE) {
          // Session starting node: a NODE-type step with no action_type, so it would
          // otherwise resolve to `actions.types.undefined` + the default icon.
          id = 'starting_node';
        } else if (step?.intent_match_theshold || step?.intent_match_confidence) {
          id = 'intent';
        } else if (step?.requisite_id) {
          id = 'question';
        } else if (step?.condition_id) {
          id = 'condition';
        } else if (step?.type === DebugStepType.WEBHOOK || step?.webhook_id) {
          id = 'webhook';
        } else if (step?.type === DebugStepType.MCP_TOOL) {
          id = 'mcp_tool';
        } else if (step?.type === DebugStepType.AUTH) {
          id = 'auth';
        } else if (step?.reminder_id || step?.reminder_seconds) {
          id = 'reminder';
        }

        return id;
      });

      // Add entity match icon at the first position
      if (entitiesMatched) {
        finalIcons?.unshift('entity');
      }

      setIcons(finalIcons);
    }
  }, [
    datasources,
    entitiesMatched,
    nodesStack,
    steps,
    tryItCollection?.fragments,
    tryItCollection?.response_code,
  ]);

  const getTitle = useCallback(
    (name: string | null) => {
      if (isLoadingResponse || responseTypeFlags?.isErrorResponse) {
        return INITIAL_TITLE;
      }

      if (responseTypeFlags?.isInterruptedResponse) {
        return interruptedResponse();
      }
      if (responseTypeFlags?.isAuthenticationContinueResponse) {
        return authenticationContinueResponse();
      }
      if (responseTypeFlags?.isVoicemailDetectedResponse) {
        return voicemailDetectedResponse();
      }
      if (responseTypeFlags?.isInstructionsResponse) {
        return knowledgeResponse(i18next.t('try_it.agent_knowledge'));
      }
      if (responseTypeFlags?.isUnknownResponse) {
        return unknownResponse(name);
      }
      if (responseTypeFlags?.isCollectionResponse) {
        return collectionResponse(collection?.name);
      }
      if (responseTypeFlags?.isBrainResponse) {
        return brainResponse(name);
      }
      if (responseTypeFlags?.isBroadcastResponse) {
        return broadcastResponse(broadcast?.name ?? i18next.t('broadcasts.deleted_broadcast'));
      }
      if (responseTypeFlags?.isCampaignResponse) {
        return campaignResponse(campaign?.name ?? i18next.t('campaigns.deleted_campaign'));
      }

      return INITIAL_TITLE;
    },
    [responseTypeFlags, collection?.name, broadcast?.name, isLoadingResponse, campaign?.name]
  );

  useEffect(() => {
    if (author_type !== 'agent') {
      setTitle(getTitle(parentNodeName));
    }
  }, [author_type, getTitle, parentNodeName]);

  const findTriggerDialog = useCallback(
    (dialogs: Dialog[]) => {
      for (const dialog of dialogs) {
        if (dialog?.nodes[0]?.name === parentNodeName) {
          return dialog;
        }
      }

      return null;
    },
    [parentNodeName]
  );

  const handleGoToNode = useCallback(() => {
    if (
      title.type === ResponseTypes.INTERRUPTED ||
      title.type === ResponseTypes.AUTHENTICATION_CONTINUE
    ) {
      return;
    }
    if (author_type === 'broadcast' && broadcast) {
      dispatch(popModal());
      navigate(`/${slug}/broadcasts/${broadcastId}`);
      return;
    }
    if (author_type === 'campaign' && campaign) {
      dispatch(popModal());
      navigate(`/${slug}/campaigns/${campaignId}/analytics`);
      return;
    }
    let dialog = dialogs?.find((d) => d.nodes.some((n) => n.node_id === id));
    let nodeId = id;

    if (!dialogs) {
      return;
    }

    const triggerDialog = findTriggerDialog(dialogs);
    if (triggerDialog) {
      dialog = triggerDialog;
      nodeId = triggerDialog?.nodes?.[0]?.node_id;
    }

    if (
      !brainId ||
      (!dialog && title.type !== ResponseTypes.COLLECTION) ||
      (!id && title.type !== ResponseTypes.COLLECTION)
    ) {
      return;
    }

    dispatch(popModal());
    dispatch(requestNodeSelection(nodeId));

    if (title.type === ResponseTypes.COLLECTION) {
      navigate(`/${slug}/knowledge-bases/${collection.collection_id}`);
    } else {
      navigate(`/${slug}/ai-agents/${brainId}/workflows/dialogs/${dialog.dialog_id}`);
    }
  }, [
    author_type,
    campaign,
    broadcast,
    dialogs,
    id,
    findTriggerDialog,
    brainId,
    title.type,
    dispatch,
    navigate,
    slug,
    broadcastId,
    campaignId,
    collection?.collection_id,
  ]);

  return { title, icons, handleGoToNode };
};
